import React, { useState } from 'react';
import { formatDate, toLocalISO } from '../../utils/dateFormat';
import { useVatMode } from '../../hooks/useVatMode';
import { getOrderNumber } from '../../utils/orderTitle';
import HireMoneyWarningDialog from './HireMoneyWarningDialog';
import { BTN } from '../../constants/buttons';
import { FIELD_LABEL_CLASS } from '../../constants/inputs';
import { hireMoneyChangeSummary } from '../../utils/hireMoneyWarning';
// 💷 THE per-line price choice (owner 2026-09-28) and every off-hire price
// rule — shared with `OffHireAllDialog.js`, never copied. The rules lived in
// THIS file until 2026-09-28; `OffHirePriceChoice.js` is their only home, so
// import them from there. Nothing is re-exported from here any more: the last
// four test-only re-exports were deleted 2026-10-21 (pre-deletion SHA
// `b351abc1`).
import OffHirePriceChoice, {
  initialPriceChoiceState,
  buildOffHirePriceChoice,
  priceChoiceAfterDateChange,
  describeActualOption,
} from './OffHirePriceChoice';
import OffHireReturnDateChoice, { initialReturnDateState, returnDateOf } from './OffHireReturnDate';
import { EM_DASH } from '../../utils/moneyOrAbsent';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../../constants/overlayLayers';

/**
 * Modal for Extend, Off-Hire, and Re-Hire actions on hire items.
 * Uses Tailwind dark: classes for reliable dark mode support.
 *
 * 🔑 Extend is a MONEY action — `PATCH /:itemId/extend` recomputes
 * `order_items.line_total`. Owner ruling 2026-08-18: warn before it applies,
 * every time, sharper when the order is already closed. The date form below
 * validates and stages the change; `pendingExtend` gates the actual
 * `onConfirm` call behind `HireMoneyWarningDialog`, so cancelling the
 * warning issues no request at all. Off-Hire and Re-Hire never touch the
 * planned dates and must never route through this gate — see
 * `utils/hireMoneyWarning.js`.
 *
 * 🔑 **Off-hire is the OPPOSITE shape (2026-09-01 owner rulings).** The
 * original ask was a date picker, defaulting to today, past AND future
 * ruled in, with a live price comparison and an optional price override.
 * Restated same day: *"the default is to CHARGE the original price for the
 * hire dates specified in the hire order BUT to flag the potential
 * discrepancy to the buyer so they can edit the dates and override the
 * price."* An off-hire date is evidence about PAPERWORK, not money — so
 * this pane only ever SHOWS the estimated discrepancy
 * (`hireOverrunDelta`, whole-span, signed, never persisted); moving the
 * price is a deliberate, REQUIRED-NOTE opt-in, attributed server-side to the
 * caller — never automatic, and
 * never gated behind `HireMoneyWarningDialog` (see that component's own
 * docblock: off-hire moves no PLANNED date).
 *
 * 💷 **THE BOX IS THE FINAL PRICE, NOT A DELTA (owner 2026-09-27):** *"'Adjust
 * the price for this return' should be changed to 'Adjust final price for
 * this return' and the total entered IS the final price but the server can
 * calculate the offset to achieve this price."* Follow-up (2026-09-28): the
 * box PREFILLS with the cost at the RETURN DATE the user has picked PLUS the
 * hire's earlier adjustments, and FOLLOWS the return date until the user
 * types a figure of their own — see `prefillFinalPrice`. So the payload is
 * `adjustment: { final_price, note }`
 * — never an amount — and `PATCH /api/hires/:itemId/off-hire` derives and
 * stores `offset = final_price − (line_total + prior adjustment rows)`. The
 * footer's preview of that offset (`describeFinalPricePreview`) is a
 * CONVENIENCE built from the row's `line_total` + `adjustment_total`; the
 * server is the authority and its response says what it actually stored.
 * A final PRICE is never negative (the offset may be), so this field no
 * longer passes `allowNegative`.
 *
 * ⚖️ **THE PRICE CHOICE (owner 2026-09-28)** replaced the "Adjust final price"
 * checkbox with three radios — Keep original (DEFAULT, sends no adjustment),
 * Use actual (the cost over hire_from → return date), Set final price (the
 * box above). The component and its rules live in `OffHirePriceChoice.js`.
 */

/**
 * The single-line dialog's test ids for the shared price choice. The Set-final-
 * price parts keep the `offhire-adjust-*` ids they have carried since the
 * 2026-09-01 "Adjust" section; everything else is `offhire-<part>`.
 */
const SINGLE_LINE_LEGACY_IDS = { amount: 'offhire-adjust-amount', note: 'offhire-adjust-note', preview: 'offhire-adjust-preview', error: 'offhire-adjust-error' };
const singleLineTestId = (part) => SINGLE_LINE_LEGACY_IDS[part] || `offhire-${part}`;

function HireActionModal({ item, action, onConfirm, onCancel, loading, initialReturnDate = null, initialPriceChoice = null }) {
  const { formatMoney } = useVatMode();
  const [newDate, setNewDate] = useState('');
  const [error, setError] = useState('');
  // Staged { new_hire_to } awaiting the money-warning confirmation — null
  // until the date form validates, cleared the instant onConfirm is called
  // (success or failure both leave this modal's own state alone; a failed
  // save re-opens the date form the same way it always has).
  const [pendingExtend, setPendingExtend] = useState(null);
  // Off-hire pane state — see the docblock above for why this branch never
  // stages anything behind HireMoneyWarningDialog the way extend does.
  // 🔀 Owner report, 2026-08-31 (Jake Dimmer): reopening this dialog always
  // showed today, never a date already recorded on the item — read as "it
  // didn't save" even when it did. `item.actual_return_date` is nullable
  // (null / '' / a stored value), and a plain truthy check already
  // collapses null and '' onto the SAME correct "no date" outcome — it
  // does not need `Number.isFinite`-style separate handling the way a
  // legitimate falsy VALUE (a `0`) would, because an empty string is never
  // a stored date. Matches the existing `item.actual_return_date &&`
  // precedent a few lines below (the re-hire pane's "Previously returned
  // on X" line).
  // `initialReturnDate` (owner ruling 2026-10-05, "Point them to Off hire"):
  // a Gantt drag that shortens a hire to a date already PASSED is not sent to
  // `/shorten` (future-only on the server); the page opens THIS off-hire pane
  // pre-filled with the dragged date instead, so the price choice below still
  // happens here and off-hire still never auto-reprices. Absent → unchanged.
  // 🔀 2026-10-19 (U2): the date is now a CHOICE — "When did it come back?"
  // (`OffHireReturnDate.js`). A preset (the Gantt date, or a stored
  // `actual_return_date`) selects the row it matches; with none, "As booked".
  // The three rulings this reconciles are named at that file's docblock.
  const [today] = useState(() => toLocalISO(new Date()));
  const [returnState, setReturnState] = useState(() => initialReturnDateState({
    hireTo: item && item.hire_to,
    presetDate: initialReturnDate || (item && item.actual_return_date) || null,
    today,
  }));
  const returnDate = item ? returnDateOf(returnState, item.hire_to, today) : '';
  const firstReturnDate = () => returnDate;
  // The price choice (owner 2026-09-28): `{ choice, price, touched, note,
  // error }`, KEEP ORIGINAL by default — see `OffHirePriceChoice.js`.
  // `initialPriceChoice` (optional, 2026-10-05) pre-selects another radio.
  // ONE caller: `HiresPage`'s past-date shorten route passes
  // `PRICE_CHOICE_ACTUAL` (owner ruling 2026-10-05, option A — the ruling is
  // quoted at `openOffHireModal`); every other Off hire passes nothing and
  // keeps "Keep original". It goes through `priceChoiceAfterDateChange`, the SAME rule a date
  // change applies, so an unavailable/matching "Use actual" falls back to
  // Keep original and an untouched Set-final-price box is prefilled. Absent →
  // the default state exactly as before.
  const [priceChoice, setPriceChoice] = useState(() => (initialPriceChoice
    ? priceChoiceAfterDateChange(item, { ...initialPriceChoiceState(), choice: initialPriceChoice }, firstReturnDate(), formatMoney)
    : initialPriceChoiceState()));
  const patchPriceChoice = (patch) => setPriceChoice((prev) => ({ ...prev, ...patch }));

  if (!item || !action) return null;

  const isExtend = action === 'extend';
  const isReHire = action === 're-hire';
  const isOffHire = !isExtend && !isReHire;
  const title = isExtend ? 'Extend Hire' : isReHire ? 'Bring Back On Hire' : 'Mark Off Hired';

  // For extend: min date is day after current hire_to. 🪤 A legacy line with
  // NO end date (the "Set end date" path, 2026-09-28) made this
  // `new Date('nullT00:00:00')` → "NaN-NaN-NaN"; its min is the FIRST day of
  // hire instead (inclusive end: a one-day hire ends on its start), and with
  // neither date there is no min. Pinned by HireActionModal.setEndDateMin.test.js.
  const minDate = isExtend ? (() => {
    if (!item.hire_to) return item.hire_from || undefined;
    const d = new Date(item.hire_to + 'T00:00:00');
    d.setDate(d.getDate() + 1);
    return toLocalISO(d);
  })() : undefined;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (isExtend) {
      if (!newDate) {
        setError('Please select a new end date');
        return;
      }
      if (newDate <= item.hire_to) {
        setError('New end date must be after the current end date');
        return;
      }
      // Gate, don't send: the money warning decides whether onConfirm fires.
      setPendingExtend({ new_hire_to: newDate });
    } else if (isReHire) {
      onConfirm({});
    } else {
      // Off-hire: the return date defaults to today but is fully editable —
      // past and future both ruled in (owner ruling). No PLANNED date
      // moves, so this never routes through HireMoneyWarningDialog. Any
      // price movement is SHOWN only (the price choice); applying one is an
      // explicit, noted, attributed opt-in — never automatic (owner ruling,
      // 2026-09-01: "an off-hire date is evidence about paperwork, not
      // money").
      if (!returnDate) {
        setError('Please select a return date');
        return;
      }
      // 💷 KEEP ORIGINAL (the default) carries no `adjustment` at all and the
      // server changes no money (OFF-HIRE NEVER AUTO-REPRICES). Use actual and
      // Set final price go through the ONE shared builder — the same one the
      // Off hire all dialog uses per line.
      const built = buildOffHirePriceChoice(item, returnDate, priceChoice, formatMoney);
      if (built.noteOwed) {
        // Confirm stays LIVE (owner 2026-10-19: "someone may feel the button is
        // not working"); with a note owed it nudges the note box instead.
        setPriceChoice((prev) => ({ ...prev, nudge: (prev.nudge || 0) + 1 }));
        return;
      }
      if (built.error) {
        patchPriceChoice({ error: built.error });
        return;
      }
      onConfirm(built.adjustment
        ? { return_date: returnDate, adjustment: built.adjustment }
        : { return_date: returnDate });
    }
  };

  const rateType = item.hire_rate_type || 'total';
  const rateLabel = rateType === 'daily' ? 'Daily' : rateType === 'weekly' ? 'Weekly' : 'Total';
  const rateAmount = rateType === 'daily' ? item.daily_rate : rateType === 'weekly' ? item.weekly_rate : item.total_hire_rate;

  // Off hire summary: planned = what "Keep booked price" charges — a dash when
  // no price is stored (review of c29f392c). 🔻 The "Cost to return date" row is
  // DELETED (owner 2026-10-19: it counted to today whatever date was picked);
  // the price block below says what the chosen date costs.
  const offHireSummary = isOffHire ? { planned: describeActualOption(item, returnDate, formatMoney).original } : null;

  // Header background and title color classes based on action type
  const headerBgClass = isExtend
    ? 'bg-blue-50 dark:bg-blue-900/20'
    : isReHire
      ? 'bg-indigo-50 dark:bg-indigo-900/20'
      : 'bg-green-50 dark:bg-green-900/20';

  const titleColorClass = isExtend
    ? 'text-blue-800 dark:text-blue-300'
    : isReHire
      ? 'text-indigo-800 dark:text-indigo-300'
      : 'text-green-800 dark:text-green-300';

  return (
    <>
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4 bg-black/50 ${MODAL_OVERLAY}`} data-testid="hire-action-modal">
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-0 md:mx-4 max-h-[90vh] overflow-y-auto ${MODAL_PANEL_MOBILE_CAP}`}>
        {/* Header */}
        {/* ONE line, title · description (owner 2026-10-20, R5 — saves ~40px).
            SHARED by Extend / Bring Back On Hire / Mark Off Hired; the owner
            accepted the shared change. The title never shrinks; the
            description truncates. */}
        <div
          className={`flex items-baseline gap-1.5 min-w-0 px-4 md:px-5 py-2.5 border-b border-gray-200 dark:border-gray-700 ${headerBgClass}`}
          data-testid="hire-action-header"
        >
          <h3 className={`shrink-0 text-base font-semibold ${titleColorClass}`}>{title}</h3>{' '}
          <p className="min-w-0 truncate text-sm text-gray-600 dark:text-gray-400" data-testid="hire-action-description">
            · {item.description}
          </p>
        </div>

        {/* Item details */}
        <div className="px-4 md:px-5 py-3 border-b border-gray-100 dark:border-gray-700 text-sm space-y-1.5">
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Order:</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{getOrderNumber(item)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Supplier:</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{item.supplier_name || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Hire Period:</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(item.hire_from)} → {formatDate(item.hire_to)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">{rateLabel} Rate:</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{formatMoney(rateAmount)}</span>
          </div>
          {isOffHire ? (
            // Off hire (owner 2026-10-08): the summary FOLLOWS the return date.
            // DISPLAY ONLY — what is saved is still the price choice below,
            // Keep original by default (off-hire never auto-reprices). Both
            // figures come from `describeActualOption`, the same calculation
            // the "Keep original" / "Use actual" rows show, so they cannot
            // disagree with the choice.
            <>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Planned cost:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100" data-testid="offhire-planned-cost">
                  {offHireSummary.planned === null ? EM_DASH : formatMoney(offHireSummary.planned)}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Expected Cost:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{formatMoney(item.expected_cost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Current Cost:</span>
                <span
                  className={`font-medium ${
                    item.current_cost > item.expected_cost
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-900 dark:text-gray-100'
                  }`}
                >
                  {formatMoney(item.current_cost)}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Action form */}
        {/* `noValidate` (house convention, `OrderForm`): the refusals here
            are the INLINE ones — a focused `type="number"` box carrying a
            negative would otherwise let Enter show the browser's native
            `min="0"` bubble instead of "A final price cannot be negative". */}
        <form onSubmit={handleSubmit} noValidate className="px-4 md:px-5 py-4">
          {isExtend ? (
            <div className="mb-4">
              <label className={FIELD_LABEL_CLASS}>
                New last day of hire
              </label>
              <input
                type="date"
                value={newDate}
                onChange={(e) => { setNewDate(e.target.value); setError(''); }}
                min={minDate}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                data-testid="extend-date-input"
                autoFocus
              />
              {item.hire_extended_count > 0 && (
                <p className="text-xs mt-1 text-purple-600 dark:text-purple-400">
                  This item has been extended {item.hire_extended_count} time{item.hire_extended_count !== 1 ? 's' : ''} already.
                  {item.original_hire_to && ` Original end date: ${formatDate(item.original_hire_to)}`}
                </p>
              )}
            </div>
          ) : isReHire ? (
            <div className="mb-4 space-y-3">
              <div className="border rounded-lg p-3 bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-700">
                <p className="text-sm text-indigo-800 dark:text-indigo-300">
                  This will bring the item back on hire and clear the return date.
                </p>
              </div>
              {/* Warning if hire_to date is in the past */}
              {item.hire_to && item.hire_to < toLocalISO(new Date()) && (
                <div
                  className="border rounded-lg p-3 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
                  data-testid="rehire-date-warning"
                >
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                        Hire dates are in the past
                      </p>
                      <p className="text-xs mt-1 text-amber-800 dark:text-amber-300">
                        The hire end date ({formatDate(item.hire_to)}) has passed. Don't forget to update the hire dates with the new dates after bringing this item back on hire.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {item.actual_return_date && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Previously returned on {formatDate(item.actual_return_date)}.
                </p>
              )}
              {/* Adjustments SURVIVE a re-hire, untouched, but are SHOWN
                  (spec Q5: no prompt, no deletion). Re-hire moves no
                  planned date, so nothing reprices; the user is simply told
                  one exists. 🪤 `adjustment_total` is `NULL` when absent and
                  a genuine SIGNED number otherwise, gated on
                  `Number.isFinite`, never a falsy test. A single negative
                  adjustment is truthy and would survive `&&` fine — the
                  real bite is `0`: `adjustment_total` is a SUM across every
                  adjustment row on the item (`server/routes/hires.js`), so
                  TWO offsetting adjustments (say +£50 then −£50) net to
                  exactly `0` while a real, attributed adjustment genuinely
                  exists — `item.adjustment_total &&` would silently hide
                  it. `Number.isFinite(0)` is `true`; that is the whole
                  reason this is the guard and not a truthiness check. */}
              {Number.isFinite(item.adjustment_total) && (
                <div
                  className="border rounded-lg p-3 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
                  data-testid="rehire-adjustment-note"
                >
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    This item has a hire price adjustment of {formatMoney(item.adjustment_total)}. Bringing it back on hire keeps it.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="mb-4 space-y-3">
              {/* "When did it come back?" (owner 2026-10-19) replaces the date
                  picker, its hint, the "overdue by N days" line and the
                  "Booked … returning …" line. */}
              <OffHireReturnDateChoice
                hireTo={item.hire_to}
                today={today}
                state={returnState}
                onChange={(nextState) => {
                  const next = returnDateOf(nextState, item.hire_to, today);
                  setReturnState(nextState);
                  setError('');
                  // An untouched Set-final-price box FOLLOWS the return
                  // date (owner 2026-09-28); the rest of the choice is
                  // derived from the date on render.
                  // A move across early / on time / late resets the choice to
                  // Keep (owner ruling A, 2026-10-19) — hence `returnDate`, the
                  // date being left.
                  setPriceChoice((prev) => priceChoiceAfterDateChange(item, prev, next, formatMoney, returnDate));
                }}
                tid={singleLineTestId}
              />

              {/* The price — the three options on EVERY return (owner
                  2026-10-20), Keep booked price by default. Charge actual hire
                  and Set final price are explicit, noted, attributed
                  overrides, and what stands in for the money-warning gate this
                  action never routes through (see the docblock at the top). */}
              <OffHirePriceChoice
                item={item}
                returnDate={returnDate}
                state={priceChoice}
                onChange={patchPriceChoice}
                tid={singleLineTestId}
                formatMoney={formatMoney}
              />
            </div>
          )}

          {error && (
            <div className="mb-3 text-sm border rounded-lg p-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700">
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              disabled={loading || !!pendingExtend}
            >
              Cancel
            </button>
            <button
              type="submit"
              // All three modes commit, so all three are the green Save/commit
              // intent (owner ruling 2026-09-25 — Extend Hire and Bring Back On
              // Hire were blue). One class from constants/buttons.js; no
              // hand-rolled fill, and its dark /70 fill is the one these
              // strings already copied.
              className={`flex-1 justify-center ${BTN.confirm} ${loading ? 'opacity-50 cursor-wait' : ''}`}
              disabled={loading || !!pendingExtend}
              data-testid="modal-confirm-btn"
            >
              {loading ? 'Processing...' : (isExtend ? 'Extend Hire' : isReHire ? 'Bring Back On Hire' : 'Confirm Off Hired')}
            </button>
          </div>
        </form>
      </div>
    </div>

    {/* Money warning — gates the actual onConfirm call. Cancelling clears
        pendingExtend and returns to the date form above; it never closes
        this whole modal or issues a request.
        Both commit buttons run the SAME staged data; "Extend & view order"
        additionally passes { viewOrder: true } as onConfirm's 2nd argument —
        the page's handleExtend decides what to do with it (navigate after
        res.ok), never this component. Neither button clears pendingExtend
        until the click fires, so a failed attempt (the page's own catch
        alerts and leaves modalItem set) still lands back on the date form —
        see HireActionModal.js's own docblock for why this surface's "safe
        landing" is the date form rather than a re-opened money dialog. */}
    {isExtend && (
      <HireMoneyWarningDialog
        open={!!pendingExtend}
        action="extend"
        item={item}
        busy={loading}
        confirmLabel="Quick extend"
        altLabel="Extend & view order"
        altVariant="confirm"
        onAlt={() => {
          const data = pendingExtend;
          setPendingExtend(null);
          if (data) onConfirm(data, { viewOrder: true });
        }}
        onConfirm={() => {
          const data = pendingExtend;
          setPendingExtend(null);
          if (data) onConfirm(data);
        }}
        onCancel={() => setPendingExtend(null)}
      >
        {pendingExtend && (() => {
          const summary = hireMoneyChangeSummary('extend', item, pendingExtend.new_hire_to, formatMoney);
          return (
            <div className="text-sm space-y-1.5" data-testid="modal-extend-money-details">
              {summary && summary.rows.map((row) => (
                <div className="flex justify-between" key={row.label}>
                  <span className="text-gray-500 dark:text-gray-400">{row.label}</span>
                  <span className={`font-medium ${row.highlight ? 'text-blue-700 dark:text-blue-400' : 'dark:text-gray-200'}`}>
                    {row.value}
                  </span>
                </div>
              ))}
              {summary && summary.sentence && (
                <p
                  className={`rounded-lg border p-2 ${
                    summary.offer
                      ? 'text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                      : 'text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600'
                  }`}
                  data-testid="modal-extend-money-sentence"
                >
                  {summary.sentence}
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="modal-extend-money-explainer">
                Both buttons set the new date and update the price. Cancel makes no change.
              </p>
            </div>
          );
        })()}
      </HireMoneyWarningDialog>
    )}
    </>
  );
}

export default HireActionModal;
